import React, { useEffect, useMemo, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import api from '../../Services/api';
import ProductLookupFilters from './ProductLookupFilters';
import ProductLookupTable from './ProductLookupTable';
import { LookupRow, NormalizedLookupRow } from './types';
import GridViewToggle from '../GridUsuario/GridViewToggle';
import { useMobileGridView } from '../GridUsuario/useMobileGridView';
import '../GridUsuario/index.css';
import '../CustomRegistrationForm/custom-registration-modal.css';
import './product-lookup-grid.css';

const PER_PAGE = 10;
const COL_ACTIVE = 1;
const COL_DESCRIPTION = 2;
const COL_ALT_ID = 3;

const normalizeRow = (row: LookupRow): NormalizedLookupRow => {
  const values = Object.values(row);
  const activeRaw = values[COL_ACTIVE];
  return {
    ...row,
    _id: (values[0] as string | number) ?? '',
    _active: activeRaw === 1 || activeRaw === true || activeRaw === '1',
    _description: String(values[COL_DESCRIPTION] ?? ''),
    _alternativeId: String(values[COL_ALT_ID] ?? ''),
  };
};

export interface GridProductLookupProps {
  endPoint: string;
  params?: string;
  fetchVariant?: 'dataPayload' | 'subcategoryId' | 'plainList';
  subcategoryId?: string;
  listTitle: string;
  listSubtitle?: string;
  newButtonLabel?: string;
  searchPlaceholder?: string;
  showNewButton?: boolean;
  initialActiveFilter?: string;
  PegarValorGrid: (row: LookupRow) => void;
  Iconeadd: () => void;
  Iconeditar: (row: LookupRow) => void;
  onDelete?: (row: LookupRow) => void;
}

const GridProductLookup = ({
  endPoint,
  params = '',
  fetchVariant = 'dataPayload',
  subcategoryId = '',
  listTitle,
  listSubtitle,
  newButtonLabel = 'Incluir Novo',
  searchPlaceholder = 'Pesquisar por id, descrição ou identificador...',
  showNewButton = true,
  initialActiveFilter = '1',
  PegarValorGrid,
  Iconeadd,
  Iconeditar,
  onDelete,
}: GridProductLookupProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState<NormalizedLookupRow[]>([]);
  const [allData, setAllData] = useState<NormalizedLookupRow[]>([]);
  const [sourceData, setSourceData] = useState<NormalizedLookupRow[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [pagAtual, setPagAtual] = useState(1);
  const [forcePage, setForcePage] = useState(0);

  const [search, setSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [active, setActive] = useState(initialActiveFilter);
  const [filterActive, setFilterActive] = useState(initialActiveFilter);
  const [description, setDescription] = useState('');
  const [alternativeidentifier, setAlternativeidentifier] = useState('');
  const { mobileViewMode, toggleMobileViewMode, viewModeClass } = useMobileGridView();

  type SearchPayload = {
    active: string;
    description: string;
    alternativeidentifier: string;
    search: string;
    params: string;
  };

  const buildPayload = (overrides?: Partial<SearchPayload>): SearchPayload => ({
    active,
    description,
    alternativeidentifier,
    search,
    params,
    ...overrides,
  });

  const applyPage = (data: NormalizedLookupRow[], pageOffset: number) => {
    const slice = data.slice(pageOffset, pageOffset + PER_PAGE);
    setTableData(slice);
    setPageCount(Math.ceil(data.length / PER_PAGE) || 0);
    setAllData(data);
  };

  const filterRows = (data: NormalizedLookupRow[], payload: SearchPayload) =>
    data.filter((row) => {
      if (payload.active) {
        if (payload.active === '1' && !row._active) return false;
        if (payload.active === '0' && row._active) return false;
      }
      if (
        payload.description &&
        !row._description.toLowerCase().includes(payload.description.toLowerCase())
      ) {
        return false;
      }
      if (
        payload.alternativeidentifier &&
        !row._alternativeId
          .toLowerCase()
          .includes(payload.alternativeidentifier.toLowerCase())
      ) {
        return false;
      }
      if (payload.search) {
        const haystack = JSON.stringify(row).toLowerCase();
        if (!haystack.includes(payload.search.toLowerCase())) return false;
      }
      return true;
    });

  const applyPlainListFilters = (
    base: NormalizedLookupRow[],
    overrides?: Partial<SearchPayload>,
    pageOffset = 0,
    resetPage = true,
  ) => {
    if (resetPage) {
      setPagAtual(1);
      setForcePage(0);
      pageOffset = 0;
    }
    const payload = buildPayload(overrides);
    const filtered = filterRows(base, payload);
    applyPage(filtered, pageOffset);
  };

  const fetchData = async (
    overrides?: Partial<SearchPayload>,
    pageOffset = 0,
    resetPage = true,
  ) => {
    setIsLoading(true);

    if (resetPage) {
      setPagAtual(1);
      setForcePage(0);
      pageOffset = 0;
    }

    const payload = buildPayload(overrides);

    try {
      if (fetchVariant === 'plainList') {
        const res = await api.get(endPoint);
        const raw: LookupRow[] = res.data.data || [];
        const data = raw.map(normalizeRow);
        setSourceData(data);
        if (res.data.status === 200) {
          applyPlainListFilters(data, overrides, pageOffset, resetPage);
        } else {
          applyPlainListFilters([], overrides, pageOffset, resetPage);
        }
      } else {
        const res =
          fetchVariant === 'subcategoryId' && subcategoryId
            ? await api.get(endPoint, { params: { id: subcategoryId } })
            : await api.get(endPoint, {
                params: { data: [payload] },
              });

        const raw: LookupRow[] = res.data.data || [];
        const data = raw.map(normalizeRow);

        if (res.data.status === 200) {
          applyPage(data, pageOffset);
        } else {
          applyPage([], pageOffset);
        }
      }
    } catch {
      if (fetchVariant === 'plainList') {
        setSourceData([]);
        applyPlainListFilters([], overrides, pageOffset, resetPage);
      } else {
        applyPage([], pageOffset);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (fetchVariant === 'subcategoryId' && !subcategoryId) {
      applyPage([], 0);
      setIsLoading(false);
      return;
    }
    fetchData(undefined, 0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endPoint, params, fetchVariant, subcategoryId]);

  const handleFilterActiveChange = (value: string) => {
    setFilterActive(value);
    setActive(value);
    if (fetchVariant === 'plainList') {
      applyPlainListFilters(sourceData, { active: value }, 0, true);
    } else {
      fetchData({ active: value }, 0, true);
    }
  };

  const handlePageClick = (event: { selected: number }) => {
    const selectedPage = event.selected;
    const newOffset = selectedPage * PER_PAGE;
    setPagAtual(selectedPage + 1);
    setForcePage(selectedPage);
    setTableData(allData.slice(newOffset, newOffset + PER_PAGE));
  };

  const handleSearch = () => {
    if (fetchVariant === 'plainList') {
      applyPlainListFilters(sourceData, undefined, 0, true);
    } else {
      fetchData(undefined, 0, true);
    }
  };

  const handleClear = () => {
    setSearch('');
    setFilterActive(initialActiveFilter);
    setActive(initialActiveFilter);
    setDescription('');
    setAlternativeidentifier('');
    setShowAdvanced(false);
    const cleared = {
      active: initialActiveFilter,
      description: '',
      alternativeidentifier: '',
      search: '',
    };
    if (fetchVariant === 'plainList') {
      applyPlainListFilters(sourceData, cleared, 0, true);
    } else {
      fetchData(cleared, 0, true);
    }
  };

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        search ||
          description ||
          alternativeidentifier ||
          active !== initialActiveFilter,
      ),
    [search, description, alternativeidentifier, active, initialActiveFilter],
  );

  const activeCount = allData.filter((r) => r._active).length;

  const handleSelect = (row: NormalizedLookupRow) => {
    PegarValorGrid(row);
  };

  const handleEdit = (row: NormalizedLookupRow) => {
    Iconeditar(row);
  };

  return (
    <div className="product-lookup-grid-embed">
      <div className={`grid-usuario${viewModeClass}`}>
        <div className="grid-usuario__hero">
          <div className="grid-usuario__hero-glow" aria-hidden />
          <div className="grid-usuario__hero-text">
            <h2 className="grid-usuario__hero-title">{listTitle}</h2>
            <p className="grid-usuario__hero-subtitle">
              {listSubtitle ??
                `${allData.length} registro(s), ${activeCount} ativo(s) — clique na linha para selecionar`}
            </p>
          </div>
          {showNewButton ? (
            <button type="button" className="grid-usuario__hero-btn" onClick={Iconeadd}>
              <FaPlus size={16} />
              <span>{newButtonLabel}</span>
            </button>
          ) : null}
        </div>

        <ProductLookupFilters
          searchQuery={search}
          onSearchChange={setSearch}
          onSearch={handleSearch}
          filterActive={filterActive}
          onFilterActiveChange={handleFilterActiveChange}
          totalShowing={tableData.length}
          showAdvanced={showAdvanced}
          onToggleAdvanced={() => setShowAdvanced((v) => !v)}
          hasActiveFilters={hasActiveFilters}
          onClear={handleClear}
          searchPlaceholder={searchPlaceholder}
          viewToggle={
            <GridViewToggle mode={mobileViewMode} onToggle={toggleMobileViewMode} />
          }
        />

        {showAdvanced && (
          <div className="grid-usuario__advanced">
            <div className="grid-usuario__advanced-grid">
              <label className="grid-usuario__field">
                <span>Ativo</span>
                <select value={active} onChange={(e) => setActive(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="1">Sim</option>
                  <option value="0">Não</option>
                </select>
              </label>
              <label className="grid-usuario__field">
                <span>Descrição</span>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <label className="grid-usuario__field">
                <span>Identificador alternativo</span>
                <input
                  type="text"
                  value={alternativeidentifier}
                  onChange={(e) => setAlternativeidentifier(e.target.value)}
                />
              </label>
            </div>
            <div className="grid-usuario__advanced-actions">
              <button type="button" className="grid-usuario__apply-btn" onClick={handleSearch}>
                Aplicar filtros
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="grid-usuario__loading">
            <div className="grid-usuario__skeleton-row" />
            <div className="grid-usuario__skeleton-row" />
            <div className="grid-usuario__skeleton-row" />
          </div>
        ) : tableData.length > 0 ? (
          <ProductLookupTable
            rows={tableData}
            pagAtual={pagAtual}
            pageCount={pageCount}
            totalRecords={allData.length}
            forcePage={forcePage}
            onPageChange={handlePageClick}
            onSelect={handleSelect}
            onEdit={handleEdit}
            onDelete={onDelete}
          />
        ) : (
          <div className="grid-usuario__empty">
            <h3>Nenhum registro encontrado</h3>
            <p>Ajuste os filtros ou cadastre um novo item.</p>
            {showNewButton ? (
              <button type="button" className="grid-usuario__empty-btn" onClick={Iconeadd}>
                {newButtonLabel}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default GridProductLookup;
